/**
 * "Which client is this conversation about?", as one circle in the header.
 *
 * It used to be a search field lying across its own strip under the subject: a
 * 220px input that was empty on nine threads out of ten and cost a full row of
 * the reading pane on all of them. The strip is gone, so the search moved into
 * the menu the circle opens - the same `Pop` panel every other dropdown in the
 * app uses - and the answer, once there is one, is shown as a chip on the
 * sender line rather than as text sitting inside a form control.
 *
 * The circle stays filled while a client is linked. That is the only thing left
 * on screen saying the link exists when the panel is shut, and a header of
 * identical outlines would otherwise hide it completely.
 */
import { useEffect, useState } from "react";
import { Check, Loader2, Search, Users, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailThread } from "../../../../lib/mail";
import { getClientOptions } from "../../../../lib/projects";
import { IconBtn } from "../../../ui";
import { Pop, useClosePops } from "../../../shell/Pop";

/**
 * Keys the caret owns while the search field has focus.
 *
 * `Pop` listens for these on the panel to walk its rows, which is right for a
 * menu of buttons and wrong the moment one of the rows is a text input: End
 * would jump to the last client instead of to the end of the word being typed.
 * Up and Down are deliberately NOT in here - stepping from the field into the
 * results is exactly what they should do.
 */
const CARET_KEYS = new Set(["Home", "End", "ArrowLeft", "ArrowRight"]);

export function ClientLinkButton({
  thread,
  onPatch,
}: {
  thread: MailThread;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
}) {
  const t = useT();
  const linked = thread.client?.name ?? null;

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn((open || thread.clientId) && "is-on")}
          // The linked name in the tooltip, so hovering answers "which client?"
          // without opening anything.
          title={linked ?? t("email.linkClient")}
          aria-label={t("email.linkClient")}
          aria-expanded={open}
        >
          <Users className="size-4" />
        </IconBtn>
      )}
    >
      <ClientSearch thread={thread} onPatch={onPatch} />
    </Pop>
  );
}

/** Mounted only while the panel is open, so the client list is fetched on
 *  opening rather than on every render of the reading pane. */
function ClientSearch({
  thread,
  onPatch,
}: {
  thread: MailThread;
  onPatch: (body: Record<string, unknown>) => Promise<void>;
}) {
  const t = useT();
  const close = useClosePops();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<{ id: string; name: string }[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    // The empty query runs too, and runs at once: a menu that opens onto a
    // blank panel and waits for typing is a worse field than the one this
    // replaced. Typed queries are debounced the usual 250ms.
    const timer = setTimeout(
      () => {
        getClientOptions(q.trim() || undefined)
          .then((r) => alive && setRows(r.rows))
          .catch(() => alive && setRows([]))
          .finally(() => alive && setBusy(false));
      },
      q.trim() ? 250 : 0,
    );
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  function link(clientId: string | null) {
    close();
    void onPatch({ clientId });
  }

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("email.linkClient")}</div>
        <div className="crm-menu-s crm-trunc">{thread.client?.name ?? t("email.noClient")}</div>
      </div>

      <label className="crm-msearch">
        {busy ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="size-4 shrink-0 text-muted-foreground" />
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => CARET_KEYS.has(e.key) && e.stopPropagation()}
          placeholder={t("email.clientPlaceholder")}
          autoComplete="off"
          aria-label={t("email.clientPlaceholder")}
        />
      </label>

      {rows === null ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="crm-pal-empty">{t("email.clientNoHits")}</div>
      ) : (
        rows.map((row) => (
          <button
            key={row.id}
            type="button"
            className={cn("crm-mi", row.id === thread.clientId && "is-on")}
            onClick={() => link(row.id)}
          >
            {row.id === thread.clientId ? <Check className="size-4" /> : <Users className="size-4" />}
            <span className="crm-trunc flex-1">{row.name}</span>
          </button>
        ))
      )}

      {/* Only once there is something to undo. The field this replaced could
          never take a link off a thread at all - it could only overwrite one -
          which meant a mis-picked client stayed on the conversation forever. */}
      {thread.clientId ? (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi" onClick={() => link(null)}>
            <X className="size-4" />
            {t("email.clientUnlink")}
          </button>
        </>
      ) : null}
    </>
  );
}
